import {
  AccountCategory,
  AccountCategoryCode,
} from '@account-users/account/models';
import { IAccount } from '@account-users/account/models/IAccount';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Injectable,
  Input,
  OnChanges,
  OnDestroy,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  NgbDateParserFormatter,
  NgbDateStruct,
} from '@ng-bootstrap/ng-bootstrap';
import { FormState } from '@shared';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

class ExpirationDateValidators {
  static expirationDate(control: AbstractControl): Record<string, unknown> {
    if (control?.value) {
      const { day, month, year } = control?.value;
      const d1 = new Date(year, month - 1, day).getTime();
      const d2 = new Date().setHours(0, 0, 0, 0);
      return d1 < d2 ? { wrongDate: true } : null;
    }
    return null;
  }
}

@Injectable()
export class CustomDateParserFormatter extends NgbDateParserFormatter {
  readonly delimeter = '/';
  public parse(value: string): NgbDateStruct | null {
    if (value) {
      const date = value.split(this.delimeter);
      return { day: +date[0], month: +date[1], year: +date[2] };
    }
    return null;
  }

  public format(date: NgbDateStruct | null): string {
    const { day, month, year } = date || {};
    return date
      ? `${day}${this.delimeter}${month}${this.delimeter}${year}`
      : '';
  }
}

@Component({
  selector: 'app-edit-account-details-form',
  templateUrl: './edit-account-details.component.html',
  styleUrls: ['./edit-account-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter },
  ],
})
export class EditAccountDetailsFormComponent implements OnChanges, OnDestroy {
  @Input() progress: boolean;
  @Input() accountDetails: IAccount;
  @Input() public set accountCategories(accountCategories: AccountCategory[]) {
    this._accountCategories = accountCategories;
  }
  @Output() formSubmit = new EventEmitter<{
    displayName: string;
    category: string;
    description?: string;
    expirationDate?: string;
    sbmRef?: string;
  }>();
  @Output() cancelSubmit = new EventEmitter();

  public formState = FormState;
  public editForm: FormGroup;
  private readonly _destroy$ = new Subject<void>();
  private _accountCategories: AccountCategory[] = [];
  private _hasSubscriptionId = false;

  public get accountCategory() {
    return this.editForm.get('category');
  }

  public get accountDescription() {
    return this.editForm.get('description');
  }

  public get expirationDate() {
    return this.editForm.get('expirationDate');
  }

  public get subscriptionId() {
    return this.editForm.get('subscriptionId');
  }

  public get accountCategories() {
    return this._accountCategories;
  }

  public get hasSubscriptionId() {
    return this._hasSubscriptionId;
  }

  constructor(private fb: FormBuilder) {
    this.editForm = this.fb.group({
      displayName: [
        '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(32),
        ],
      ],
      category: [
        '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(32),
        ],
      ],
      subscriptionId: [''],
      description: ['', Validators.maxLength(1000)],
      expirationDate: [null, ExpirationDateValidators.expirationDate],
    });

    this.editForm
      .get('category')
      .valueChanges.pipe(takeUntil(this._destroy$))
      .subscribe((ac) => {
        this.updateSubscriptionIdByCategory(ac as AccountCategoryCode);
      });
  }

  ngOnChanges() {
    if (this.accountDetails && this.accountCategories) {
      const {
        displayName,
        category,
        description,
        expirationDate,
      } = this.accountDetails;
      this.editForm.patchValue(
        { displayName, category, description },
        { emitEvent: false },
      );

      this.updateSubscriptionIdByCategory(category as AccountCategoryCode);

      if (expirationDate) {
        const year = new Date(expirationDate).getFullYear();
        const month = new Date(expirationDate).getMonth() + 1;
        const day = new Date(expirationDate).getDate();
        this.editForm.patchValue({
          expirationDate: { year, month: month, day },
        });
      }

      if (category !== AccountCategoryCode.Trial)
        this._accountCategories = (this._accountCategories || []).filter(
          (c) => c.code !== AccountCategoryCode.Trial,
        );
    }
  }

  public onSubmit() {
    if (this.editForm.valid) {
      const {
        displayName,
        category,
        description,
        expirationDate,
        subscriptionId,
      } = this.editForm.value;

      let accountDetails: {
        displayName: string;
        category: string;
        description?: string;
        expirationDate?: string;
        sbmRef?: string;
      } = {
        displayName,
        category,
        description: description === '' ? null : description, // textarea returns empty string when empty
        expirationDate: null, // ensures value removed when calling the PATCH endpoint
        sbmRef: subscriptionId,
      };
      if (expirationDate) {
        const { year, month, day } = expirationDate;
        accountDetails = {
          ...accountDetails,
          expirationDate: new Date(
            Date.UTC(year, month - 1, day),
          ).toISOString(),
        };
      }
      this.formSubmit.emit(accountDetails);
    }
  }

  private updateSubscriptionIdByCategory(categoryCode: AccountCategoryCode) {
    const { sbmRef } = this.accountDetails;
    this._hasSubscriptionId =
      categoryCode === AccountCategoryCode.External ||
      categoryCode === AccountCategoryCode.Trial ||
      categoryCode === AccountCategoryCode.SuiteMaxo;

    if (this._hasSubscriptionId) {
      this.subscriptionId.setValue(sbmRef, { emitEvent: false });
      const validators =
        categoryCode === AccountCategoryCode.Trial
          ? [Validators.pattern(/^[A-Za-z0-9]{16}$/)]
          : [Validators.required, Validators.pattern(/^[A-Za-z0-9]{16}$/)];

      this.subscriptionId.setValidators(validators);
    } else {
      this.subscriptionId.reset();
      this.subscriptionId.clearValidators();
    }

    this.subscriptionId.updateValueAndValidity();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
